/**
 * DropdownWithCustom — 사전 옵션 + "기타" 직접 입력 + (옵션) 자유 텍스트 토글.
 *
 * 사용 흐름:
 *   1) 옵션에서 선택: value 가 옵션값으로 설정
 *   2) "__custom__" 선택: 텍스트 입력창이 노출되고 사용자 입력값이 value 가 됨
 *   3) allowFreeText=true: 상단 토글로 "직접 입력 모드" 전환 가능 (기존 텍스트 입력 방식 유지용)
 *
 * 핵심: 부모는 onChange(string) 만 받으면 되고 내부 모드 관리 불필요.
 */
import { useEffect, useState, type ChangeEvent } from 'react'
import { FiChevronDown, FiEdit3, FiList } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

export interface DropdownOption {
  value: string
  label: string
}

interface Props {
  label?: string
  options: readonly DropdownOption[] | DropdownOption[]
  value: string
  onChange: (val: string) => void
  placeholder?: string
  customLabel?: string             // "기타" 라벨 (기본: "기타 (직접 입력)")
  allowFreeText?: boolean          // true 시 상단 토글로 자유 텍스트 모드 전환 허용
  required?: boolean
  error?: string
  inputPlaceholder?: string
  className?: string
  disabled?: boolean
  /** 추천 옵션 value 목록 — 옵션 상단 배치 + 라벨 앞에 ✨ 마커. native <select> 한계로 배지 색상은 못 입힘. */
  recommendedKeys?: string[]
}

const CUSTOM_VALUE = '__custom__'

export default function DropdownWithCustom({
  label,
  options,
  value,
  onChange,
  placeholder,
  customLabel,
  allowFreeText = false,
  required,
  error,
  inputPlaceholder,
  className,
  disabled,
  recommendedKeys,
}: Props) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('commonComponents.dropdownWithCustom.placeholder', { defaultValue: '선택하세요' })
  const resolvedCustomLabel = customLabel ?? t('commonComponents.dropdownWithCustom.customLabel', { defaultValue: '기타 (직접 입력)' })
  // 현재 value 가 옵션 목록에 있는지 — 없으면 사용자 직접 입력 상태로 추정
  const isInOptions = options.some((o) => o.value === value)

  // 추천 옵션을 상단에 배치 + 라벨 앞에 ✨ 마커 (native select 의 옵션은 스타일 제한적이라 텍스트 마커로).
  const recSet = new Set(recommendedKeys ?? [])
  const sortedOptions = recSet.size > 0
    ? [
        ...(recommendedKeys ?? []).map((k) => options.find((o) => o.value === k)).filter((o): o is DropdownOption => !!o),
        ...options.filter((o) => !recSet.has(o.value)),
      ]
    : [...options]

  // 모드: 'select' | 'free' (자유 텍스트)
  const [freeMode, setFreeMode] = useState<boolean>(
    !!value && !isInOptions && !allowFreeText ? true : false,
  )

  // "기타" 선택했지만 아직 직접 입력 시작 안 한 경우 표시용 플래그
  const [pickedCustom, setPickedCustom] = useState<boolean>(
    !!value && !isInOptions,
  )

  useEffect(() => {
    // 외부에서 value 가 옵션 항목으로 바뀌면 custom flag 해제
    if (value && isInOptions) setPickedCustom(false)
  }, [value, isInOptions])

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    if (v === CUSTOM_VALUE) {
      setPickedCustom(true)
      onChange('') // 빈 값으로 초기화 — 사용자 입력 대기
    } else {
      setPickedCustom(false)
      onChange(v)
    }
  }

  const showCustomInput = pickedCustom || (!isInOptions && !!value)
  const selectValue = isInOptions ? value : (showCustomInput ? CUSTOM_VALUE : '')

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <div className="flex items-center justify-between gap-2">
          <label className="text-sm font-medium text-ink-muted">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
          {allowFreeText && (
            <button
              type="button"
              onClick={() => {
                setFreeMode((m) => !m)
                setPickedCustom(false)
              }}
              className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-brand-primary-light transition-colors"
              aria-pressed={freeMode}
            >
              {freeMode ? <FiList size={11} /> : <FiEdit3 size={11} />}
              {freeMode
                ? t('commonComponents.dropdownWithCustom.selectFromList', { defaultValue: '목록에서 선택' })
                : t('commonComponents.dropdownWithCustom.customInput', { defaultValue: '직접 입력' })}
            </button>
          )}
        </div>
      )}

      {/* 추천 힌트 — value 미선택 + 추천 있을 때 작은 안내 (옵션 펼치면 ✨ 마커로 보임). */}
      {!freeMode && !value && recSet.size > 0 && (
        <p className="text-[10px] text-amber-600 dark:text-amber-300 -mt-0.5">
          {t('commonComponents.dropdownWithCustom.recommendedHint', { defaultValue: '✨ 상위 선택 기반 추천 옵션이 상단에 표시됩니다' })}
        </p>
      )}

      {freeMode ? (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={inputPlaceholder ?? resolvedPlaceholder}
          disabled={disabled}
          className={cn(
            'w-full rounded-lg bg-surface-card border text-ink px-4 py-2.5 text-sm',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary',
            error ? 'border-red-500/60' : 'border-surface-elevated/60 hover:border-surface-muted',
          )}
        />
      ) : (
        <>
          <div className="relative">
            <select
              value={selectValue}
              onChange={handleSelect}
              disabled={disabled}
              className={cn(
                'w-full appearance-none rounded-lg bg-surface-card border',
                'px-4 py-2.5 pr-11 text-sm',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary',
                error ? 'border-red-500/60' : 'border-surface-elevated/60 hover:border-surface-muted',
                // value 가 비어있으면 placeholder 색(흐림), 선택되면 진한 텍스트
                selectValue === '' ? 'text-ink-faint' : 'text-ink',
              )}
            >
              {/* placeholder option — hidden 으로 펼친 목록에서는 숨기고, 닫힘 상태에서만 표시 */}
              <option value="" disabled hidden>{resolvedPlaceholder}</option>
              {sortedOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="text-ink">
                  {recSet.has(opt.value) ? `✨ ${opt.label}` : opt.label}
                </option>
              ))}
              <option value={CUSTOM_VALUE} className="text-ink">{resolvedCustomLabel}</option>
            </select>
            {/* 화살표 — 강조 색상 + 약간 키움 */}
            <span
              className={cn(
                'absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none',
                'flex items-center justify-center h-6 w-6 rounded-md',
                'bg-brand-primary/10 border border-brand-primary/25 text-zinc-900 dark:text-white',
              )}
              aria-hidden="true"
            >
              <FiChevronDown size={15} strokeWidth={2.4} />
            </span>
          </div>

          {showCustomInput && (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={inputPlaceholder ?? t('commonComponents.dropdownWithCustom.customInput', { defaultValue: '직접 입력' })}
              autoFocus
              disabled={disabled}
              className={cn(
                'w-full rounded-lg bg-surface-card border text-ink px-4 py-2.5 text-sm mt-1.5',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary',
                error ? 'border-red-500/60' : 'border-surface-elevated/60 hover:border-surface-muted',
              )}
            />
          )}
        </>
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  )
}
